import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { roomApi } from '../services/api';
import { JoinRoomRequest } from '../types';
import './Home.css';
import { apiErrorMessage } from '../utils/apiError';
import { saveRoomIdentity } from '../utils/roomIdentity';

/**
 * Direct join page. Opening /join/:roomId shows a display-name form with
 * the room ID taken from the URL, so invitees can join with one tap.
 */
const JoinRoom: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState('');
  const [roomName, setRoomName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [roomChecked, setRoomChecked] = useState(false);
  const [roomExists, setRoomExists] = useState(false);

  // 招待URLを開いた時点でルームの存在とルーム名を確認する
  useEffect(() => {
    const checkRoom = async () => {
      if (!roomId) {
        setRoomChecked(true);
        return;
      }
      try {
        const data = await roomApi.getRoom(roomId);
        setRoomName(data.room.name);
        setRoomExists(true);
      } catch {
        setRoomExists(false);
      } finally {
        setRoomChecked(true);
      }
    };
    checkRoom();
  }, [roomId]);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!roomId || !displayName.trim()) return;

    setError('');
    setIsLoading(true);

    try {
      const joinData: JoinRoomRequest = { display_name: displayName };
      const result = await roomApi.joinRoom(roomId, joinData);
      saveRoomIdentity(roomId, result.user_id, displayName);
      navigate(`/room/${roomId}`);
    } catch (err: any) {
      console.error('Join room error:', err);
      if (err.response?.status === 404) {
        setError(t('errors.roomNotFound') || 'ルームが見つかりません');
      } else {
        setError(apiErrorMessage(err, t('errors.joinRoomFailed') || 'ルームへの参加に失敗しました'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!roomChecked) {
    return (
      <div className="home-container">
        <div className="home-content">
          <p className="home-description">{t('join.checking') || 'ルームを確認中...'}</p>
        </div>
      </div>
    );
  }

  if (!roomExists) {
    return (
      <div className="home-container">
        <div className="home-content">
          <h1 className="home-title">{t('join.notFoundTitle') || 'ルームが見つかりません'}</h1>
          <p className="home-description">
            {t('join.notFoundDescription') || 'このルームは存在しないか、すでに終了しています。'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="home-container">
      <div className="home-content">
        <h1 className="home-title">{t('join.title') || 'ルームに参加'}</h1>
        <p className="home-description">
          {(t('join.invitedTo') || '「{{roomName}}」に招待されています').replace('{{roomName}}', roomName)}
        </p>

        <div className="actions-container" style={{ gridTemplateColumns: '1fr', maxWidth: '480px', margin: '0 auto' }}>
          <div className="action-card">
            <h2>{t('join.formTitle') || '名前を入力して参加'}</h2>
            <form onSubmit={handleJoin} className="room-form">
              <div className="form-group">
                <label htmlFor="joinDisplayName">{t('home.joinRoom.displayName') || '表示名:'}</label>
                <input
                  type="text"
                  id="joinDisplayName"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  autoFocus
                  className="form-input"
                  placeholder={t('home.joinRoom.displayNamePlaceholder') || ''}
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="action-button join-button"
              >
                {isLoading
                  ? (t('home.joinRoom.joining') || '参加中...')
                  : (t('home.joinRoom.joinButton') || 'ルームに参加')}
              </button>
            </form>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>
    </div>
  );
};

export default JoinRoom;
